import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useParcoursDetail } from "../hooks/useParcoursDetail";
import { listLieux } from "../api/lieuxApi";
import { toLegacyParcours } from "./toLegacyParcours";
import { mapProgrammesToSillons, mapResourcesToDocuments } from "./toLegacyProgrammes";
import LegacyFrontoffice from "../LegacyFrontoffice";

declare global {
  interface Window {
    PA_DATA?: {
      parcours?: unknown[];
      artists?: unknown[];
      venues?: unknown[];
      communes?: unknown[];
      [k: string]: unknown;
    };
  }
}

/**
 * Hydrates window.PA_DATA for the legacy parcours-detail page (/parcours/:id).
 * Builds D.parcours = [legacyShape], D.artists from parcours.artists,
 * D.venues from the lieux catalog. Adds practical info + programSillons + documents.
 */
export default function HydratedLegacyDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const parcoursQ = useParcoursDetail(id);
  const lieuxQ = useQuery({
    queryKey: ["lieux", "all-summary"],
    queryFn: () => listLieux({ page: 0, size: 500, sort: "name,asc" }),
  });

  const hydrated = useMemo(() => {
    if (!parcoursQ.data) return false;
    if (!window.PA_DATA) window.PA_DATA = {};

    const p = parcoursQ.data;
    const base = toLegacyParcours(p);

    window.PA_DATA.parcours = [
      {
        ...base,
        accessibility: p.accessibility,
        contact: p.contact,
        secondaryContact: p.secondaryContact,
        website: p.website,
        horaires: deriveHoraires(p.dates),
        programSillons: mapProgrammesToSillons(p.programmes ?? []),
        documents: mapResourcesToDocuments(p.resources ?? []),
      },
    ];
    window.PA_DATA.programSillons = window.PA_DATA.parcours[0]
      ? (window.PA_DATA.parcours[0] as { programSillons?: unknown[] }).programSillons ?? []
      : [];

    if (p.mainCommune) {
      window.PA_DATA.communes = [
        { id: p.mainCommune.toLowerCase(), name: p.mainCommune, region: p.region ?? "Belgique", lonlat: [4.55, 50.66] },
      ];
    } else {
      window.PA_DATA.communes = [];
    }

    // Derive D.artists from the parcours' artists (used by legacy filters/cards).
    window.PA_DATA.artists = (p.artists ?? []).map((a) => ({
      id: a.id,
      name: a.name,
      discipline: "",
      bio: a.description ?? "",
      photo: a.photoLink ?? "",
    }));

    if (lieuxQ.data) {
      window.PA_DATA.venues = lieuxQ.data.content.map((l) => {
        const lat = l.address?.latitude;
        const lng = l.address?.longitude;
        const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
        return {
          id: l.id,
          name: l.name,
          type: "",
          commune: l.address?.city ?? "",
          street: l.address?.street ?? "",
          postcode: l.address?.postcode ?? "",
          // null => la carte géocodera depuis l'adresse (Belgique) au chargement.
          lonlat: hasCoords ? [lng as number, lat as number] : null,
        };
      });
    }
    return true;
  }, [parcoursQ.data, lieuxQ.data]);

  if (parcoursQ.isLoading && !hydrated) return <Status>Chargement…</Status>;
  if (parcoursQ.isError) return <Status error>Parcours introuvable.</Status>;
  return <LegacyFrontoffice />;
}

const MONTHS_FR_BE = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const WEEKDAY_FMT = new Intl.DateTimeFormat("fr-BE", { weekday: "long", timeZone: "UTC" });

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function dateParts(iso: string): { y: number; m: number; d: number; wd: string } | null {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d, wd: WEEKDAY_FMT.format(new Date(Date.UTC(y, m - 1, d))) };
}

/** Format an ISO date (yyyy-mm-dd) as Belgian French, e.g. "Vendredi 8 août 2026". */
function fmtDayFrBe(iso: string): string {
  const p = dateParts(iso);
  if (!p) return iso;
  return cap(`${p.wd} ${p.d} ${MONTHS_FR_BE[p.m - 1]} ${p.y}`);
}

/** Format a span between two ISO dates, e.g. "Du vendredi 8 au samedi 9 août 2026". */
function fmtRangeFrBe(startIso: string, endIso: string): string {
  const a = dateParts(startIso);
  const b = dateParts(endIso);
  if (!a || !b) return `${fmtDayFrBe(startIso)} → ${fmtDayFrBe(endIso)}`;
  const end = `${b.wd} ${b.d} ${MONTHS_FR_BE[b.m - 1]} ${b.y}`;
  if (a.y === b.y && a.m === b.m) return cap(`du ${a.wd} ${a.d} au ${end}`);
  if (a.y === b.y) return cap(`du ${a.wd} ${a.d} ${MONTHS_FR_BE[a.m - 1]} au ${end}`);
  return cap(`du ${a.wd} ${a.d} ${MONTHS_FR_BE[a.m - 1]} ${a.y} au ${end}`);
}

type Slot = {
  date: string;
  startTime?: string | null;
  endDate?: string | null;
  endTime?: string | null;
};

function deriveHoraires(dates?: Slot[]): string {
  if (!dates || dates.length === 0) return "";
  const sorted = [...dates].sort((a, b) => a.date.localeCompare(b.date));
  const hasTimes = sorted.some((d) => d.startTime || d.endTime);
  const hasEndDates = sorted.some((d) => d.endDate && d.endDate !== d.date);

  // No per-slot times or end-dates → clean single-day or "Du X au Y" summary.
  if (!hasTimes && !hasEndDates) {
    if (sorted.length === 1) return fmtDayFrBe(sorted[0].date);
    return fmtRangeFrBe(sorted[0].date, sorted[sorted.length - 1].date);
  }

  // Detailed listing — one line per slot.
  return sorted
    .map((d) => {
      const start = fmtDayFrBe(d.date);
      const endSameDay = !d.endDate || d.endDate === d.date;
      const end = endSameDay ? null : fmtDayFrBe(d.endDate as string);
      const s = d.startTime?.slice(0, 5);
      const e = d.endTime?.slice(0, 5);

      if (end) {
        // Multi-day slot
        const startLabel = s ? `${start} ${s}` : start;
        const endLabel = e ? `${end} ${e}` : end;
        return `Du ${startLabel} au ${endLabel}`;
      }
      // Single-day slot with optional times
      if (s && e) return `${start} · ${s}–${e}`;
      if (s) return `${start} · dès ${s}`;
      if (e) return `${start} · jusqu'à ${e}`;
      return start;
    })
    .join("\n");
}

function Status({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--font-body)",
        color: error ? "var(--red)" : "var(--ink-mute)",
        fontSize: 14,
      }}
    >
      {children}
    </div>
  );
}
